import React, { useEffect, useState } from "react";
import SegmentPage from "./components/SegmentPage";
import ContentCollection from "./components/ContentCollection";
import Arrow from "./icons/arrow";
import SearchBox from "./components/inputComponents/SearchBox";
import Checkbox from "./components/inputComponents/Checkbox";
import TextArea from "./components/inputComponents/TextArea";
import TextInput from "./components/inputComponents/TextInput";
import SmallOptionBoxWithImage from "./components/optionComponents/SmallOptionBoxWithImage";
import SmallOptionBox from "./components/optionComponents/SmallOptionBox";
import BigOptionBox from "./components/optionComponents/BigOptionBox";
import SelectedEmployee from "./components/displayComponents/SelectedEmployee";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorMessage from "./components/displayComponents/ErrorMessage";
import NoAdvisersAvailableMessage from "./components/displayComponents/NoAdvisersAvailableMessage";
import AdviserNotFoundErrorMessage from "./components/displayComponents/AdviserNotFoundErrorMessage";

import "./css/App.css";

const pageHistoryDefaultValue = {
  pageNumber: 1,
  pageId: "landingPage",
  pageName: "Landingssiden",
};

const availableOfficeListDefaultValue = {
  brandId: "",
  allOffices: [],
  phoneChannelOffices: [],
  webChannelOffices: [],
};

const chooseAdviserIconUrl = window.__chooseAdviserIconUrl__;
const getAssignedAdviserIconUrl = window.__getAssignedAdviserIconUrl__;
const inBankIconUrl = window.__inBankIconUrl__;
const onThePhoneIconUrl = window.__onThePhoneIconUrl__;
const webMeetingIconUrl = window.__webMeetingIconUrl__;
const apiBasePath = window.__apiBasePath__;
const contactCenterUrl = window.__contactCenter__;
const selectedBrandId = window.__selectedBrandId__;
const employeeQueryStringName = window.__selectedEmployeeQueryStringName__;

const GetQueryStringValue = (queryName) => {
  const query = window.location.search;
  if (query === "") {
    return;
  }

  const split = query.replace("?", "").split("&");

  for (let index = 0; index < split.length; index++) {
    const currentQuery = split[index];

    const currentQuerySplit = currentQuery.split("=");
    if (currentQuerySplit.length !== 2) {
      continue;
    }

    const currentQueryName = currentQuerySplit[0];
    const currentQueryValue = currentQuerySplit[1];

    if (currentQueryName !== queryName || currentQueryValue === "") {
      continue;
    }

    return currentQueryValue;
  }
};
const DeleteQueryString = (queryName) => {
  const query = window.location.search;
  if (query === "") {
    return;
  }

  const split = query.replace("?", "").split("&");

  let newQuery = "";

  for (let index = 0; index < split.length; index++) {
    const currentQuery = split[index];

    const currentQuerySplit = currentQuery.split("=");
    if (currentQuerySplit.length !== 2 || currentQuerySplit[0] === queryName) {
      continue;
    }

    if (newQuery !== "") {
      newQuery += "&";
    }

    newQuery += currentQuery;
  }

  window.location.search = newQuery;
};

function GenerateVirtualPageView(pageHistory, currentPageTitle) {
  if (pageHistory.length <= 1) {
    return;
  }

  // checks if suffix exists as the last part of str
  const stringEndsWith = (str, suffix) => {
    return str.indexOf(suffix, str.length - suffix.length) !== -1;
  };

  let path = "" + window.location.pathname;
  let pageTitle = "";

  for (let index = 1; index < pageHistory.length; index++) {
    const historyElement = pageHistory[index];
    const elementPageName = historyElement.pageName;

    if (elementPageName === undefined) {
      continue;
    }

    // if no page is defined, get last pageName
    if (currentPageTitle === undefined && index === pageHistory.length - 1) {
      pageTitle = elementPageName;
    }

    if (stringEndsWith(path, "/") === false) {
      path += "/";
    }

    path += elementPageName.replace(/ /g, "-");
  }

  // if current page title is defined, use that
  if (currentPageTitle !== undefined) {
    if (stringEndsWith(path, "/") === false) {
      path += "/";
    }

    path += currentPageTitle.replace(/ /g, "-");

    pageTitle = currentPageTitle;
  }

  const dataLayerObject = {
    //fixed value you need to use as trigger in GTM
    event: "virtualPageView",

    virtualPagePath: path,
    virtualPageTitle: pageTitle,
  };

  if (window.dataLayer) {
    window.dataLayer.push(dataLayerObject);
  }
}

const FetchOfficeData = () => {
  const [shouldCallApi, setShouldCallApi] = useState(false);

  const [data, setData] = useState(availableOfficeListDefaultValue);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // this triggers when callApi is changed
  useEffect(() => {
    // unless this is true, we abort
    if (shouldCallApi !== true) {
      return;
    }

    const fetchAvailableOfficesList = () => {
      setIsLoading(true);

      fetch(apiBasePath + "BookMeeting/GetBookMeetingData/" + selectedBrandId)
        .then((result) => {
          if (!result.ok) {
            setIsError(true);
            throw new Error("Something went wrong");
          }
          return result.json();
        })
        .then((data) => {
          setData(data);
        })
        .catch((error) => {
          console.error(error);
          setIsError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    fetchAvailableOfficesList();

    setShouldCallApi(false);
  }, [shouldCallApi]);

  return [
    {
      availableOfficesList: data,
      isFetchingAvailableOfficeList: isLoading,
      failedToRetrievedOfficeList: isError,
    },
    setShouldCallApi,
  ];
};

function App() {
  //#region Api calls

  // We create the fetch call this way to make sure all dependencies for the api call is in order.
  // By extracting it to its own function, we handle this.
  // Read more here: https://reactjs.org/docs/hooks-faq.html#is-it-safe-to-omit-functions-from-the-list-of-dependencies
  const [
    {
      availableOfficesList,
      isFetchingAvailableOfficeList,
      failedToRetrievedOfficeList,
    },
    shouldFetchOfficeData,
  ] = FetchOfficeData();

  // This triggers on page load
  useEffect(() => {
    // this triggers the api call
    shouldFetchOfficeData(true);
  }, [shouldFetchOfficeData]);

  // this triggers when loading status is changed
  useEffect(() => {
    // if failed to retrieve, skip the rest
    if (failedToRetrievedOfficeList === true) {
      return;
    }

    // if still loading, skip the rest
    if (isFetchingAvailableOfficeList === true) {
      return;
    }

    // a "private" function for finding and setting pre-existing employee
    const addPreExistingEmployee = (predefinedEmployee) => {
      for (
        let officeIndex = 0;
        officeIndex < availableOfficesList.allOffices.length;
        officeIndex++
      ) {
        const office = availableOfficesList.allOffices[officeIndex];

        for (
          let employeeIndex = 0;
          employeeIndex < office.employeesInOffice.length;
          employeeIndex++
        ) {
          const employee = office.employeesInOffice[employeeIndex];
          const employeeGuid = employee.employeeGuid;

          if (employeeGuid.toLowerCase() !== predefinedEmployee.toLowerCase()) {
            continue;
          }

          setSelectedEmployee(employee);
          setSelectedOffice(office);

          return true;
        }
      }

      return false;
    };

    if (availableOfficesList.allOffices.length === 0) {
      const noAvailableOfficesPageName = "Ingen tilgjengelige kontorer";
      const tempPageHistory = [
        pageHistoryDefaultValue,
        {
          pageId: "notFoundPage",
          pageNumber: 2,
          pageName: noAvailableOfficesPageName,
        },
      ];

      GenerateVirtualPageView(tempPageHistory, noAvailableOfficesPageName);
      setPageHistory(tempPageHistory);

      return;
    }

    // define the first pages in history
    let pageHistory = [
      pageHistoryDefaultValue,
      { pageId: "startPage", pageNumber: 2, pageName: "Start" },
    ];

    // check if there is any employee ids in the url
    const selectedEmployeeIdFromQuery = GetQueryStringValue(
      employeeQueryStringName
    );
    if (selectedEmployeeIdFromQuery !== undefined) {
      // if we find an id, call the "private" function above to set it as active
      let foundEmployee = addPreExistingEmployee(selectedEmployeeIdFromQuery);
      if (foundEmployee) {
        // add an additional page to history, in order to jump directly to "Hvor skal vi treffes? " page
        pageHistory.push({
          pageId: "chooseAdviser-selectChannelPage",
          pageNumber: 3,
          pageName: "Hvor skal vi treffes?",
        });
      } else {
        setFailedToFindAdviser(true);
      }
    }

    GenerateVirtualPageView(pageHistory);
    setPageHistory(pageHistory);
  }, [
    isFetchingAvailableOfficeList,
    failedToRetrievedOfficeList,
    availableOfficesList,
  ]);

  // booking meeting
  const [isBookingNewMeeting, setIsBookingNewMeeting] = useState(false);
  const [failedToBookMeeting, setFailedToBookMeeting] = useState(false);

  const bookMeeting = (model) => {
    // send booking to api
    setIsBookingNewMeeting(true);

    fetch(apiBasePath + "BookMeeting/BookNewMeeting", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(model),
    })
      .then((result) => {
        if (!result.ok) {
          setFailedToBookMeeting(true);
          throw new Error("Something went wrong");
        }
      })
      .catch((error) => {
        console.error(error);
        setFailedToBookMeeting(true);
      })
      .finally(() => {
        setIsBookingNewMeeting(false);
        addToHistory("shared-verificationOnSubmit", "bekreftelse");
      });
  };

  //#endregion

  //#region Input Validation

  const validateInput = (input) => {
    input.classList.remove("is-valid", "is-invalid");

    if (input.validity.valid === false) {
      input.classList.add("is-invalid");
      return false;
    } else {
      input.classList.add("is-valid");
      return true;
    }
  };

  const checkBookingFormFieldsValidity = () => {
    const inputsWithValidation =
      document.getElementsByClassName("needValidation");

    let isAllFieldsValid = true;
    let heightOffset = 0;

    for (let index = 0; index < inputsWithValidation.length; index++) {
      let input = inputsWithValidation[index];
      let isValid = validateInput(input);
      if (isValid) {
        switch (input.name) {
          case "customerName":
            setNameTextIsValid(true);
            break;
          case "phoneNumber":
            setPhoneTextIsValid(true);
            break;
          case "email":
            setEmailTextIsValid(true);
            break;
          default:
            break;
        }
        continue;
      } else {
        switch (input.name) {
          case "customerName":
            setNameTextIsValid(false);
            break;
          case "phoneNumber":
            setPhoneTextIsValid(false);
            break;
          case "email":
            setEmailTextIsValid(false);
            break;
          default:
            break;
        }

        // check if this is the first error
        if (isAllFieldsValid === true) {
          // it is the first error, then scroll to this error
          const boundingClientRect = input.parentNode.getBoundingClientRect();

          heightOffset = boundingClientRect.top + window.pageYOffset - 20;

          isAllFieldsValid = false;
        }
      }
    }

    window.scrollTo(0, heightOffset);

    return isAllFieldsValid;
  };

  const removeValidationClasses = () => {
    let inputsWithValidation =
      document.getElementsByClassName("needValidation");
    for (let index = 0; index < inputsWithValidation.length; index++) {
      let input = inputsWithValidation[index];
      input.classList.remove("is-valid", "is-invalid");
    }
  };

  //#endregion

  //#region SegmentPageLogic
  const [failedToFindAdviser, setFailedToFindAdviser] = useState(false);

  const [selectedOffice, setSelectedOffice] = useState(null);
  const addSelectedOffice = (officeItem, nextPageId, nextPageName) => {
    setSelectedOffice(officeItem);
    addToHistory(nextPageId, nextPageName);
  };

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const addSelectedEmployee = (employeeItem, nextPageId, nextPageName) => {
    setSelectedEmployee(employeeItem);
    addToHistory(nextPageId, nextPageName);
  };

  const addSelectedSearchResult = (
    officeItem,
    employeeItem,
    nextPageId,
    nextPageName
  ) => {
    setSelectedOffice(officeItem);
    setSelectedEmployee(employeeItem);
    addToHistory(nextPageId, nextPageName);
  };

  const [selectedChannel, setSelectedChannel] = useState(null);
  const addSelectedChannel = (channelId, nextPageId, nextPageName) => {
    setSelectedChannel(channelId);
    addToHistory(nextPageId, nextPageName);
  };

  const [selectedDate, setSelectedDate] = useState(null);
  const addSelectedDate = (dateElement, nextPageId, nextPageName) => {
    setSelectedDate(dateElement);
    addToHistory(nextPageId, nextPageName);
  };

  const [selectedTime, setSelectedTime] = useState(null);
  const addSelectedTime = (timeString, nextPageId, nextPageName) => {
    setSelectedTime(timeString);
    addToHistory(nextPageId, nextPageName);
  };

  const createBookingModel = () => {
    // create model
    const bookingModel = {
      customerName: nameTextInput.trim(),
      customerPhone: phoneTextInput.trim(),
      customerEmail: emailTextInput.trim(),

      selectedBrandGuid: selectedBrandId,
      selectedOfficeGuid:
        selectedOffice !== null ? selectedOffice.officeGuid : null,
      selectedEmployeeGuid:
        selectedEmployee !== null ? selectedEmployee.employeeGuid : null,

      selectedChannel: selectedChannel,
      selectedDate: selectedDate.date,
      selectedTime: selectedTime,

      subjectLoan: loanCheckbox,
      subjectSaving: savingCheckbox,
      subjectInsurance: insuranceCheckbox,
      subjectPrivateEconomy: privateEconomyCheckbox,
      subjectCustom: otherTextArea,
    };

    return bookingModel;
  };

  const submitBooking = () => {
    var formValidatedSuccessfully = checkBookingFormFieldsValidity();
    if (!formValidatedSuccessfully) {
      setInputIsValid(false);
      return null;
    }

    var model = createBookingModel();
    if (model !== null) {
      bookMeeting(model);
    }
  };

  const resetToStart = () => {
    resetSetValues();

    goToPageInHistory(1);

    removeValidationClasses();

    shouldFetchOfficeData(true);

    goToPageInHistory(1);

    DeleteQueryString(employeeQueryStringName);
  };

  const resetSetValues = () => {
    resetSelectedOffice();
    setSelectedEmployee(null);
    setSelectedChannel(null);
    setSelectedDate(null);
    setSelectedTime(null);

    setLoanCheckbox(false);
    setSavingCheckbox(false);
    setInsuranceCheckbox(false);
    setPrivateEconomyCheckbox(false);

    setOtherTextArea("");
    setNameTextInput("");
    setPhoneTextInput("");
    setEmailTextInput("");
    setAdviserSearchWord("");
    setSearchResult([]);
  };
  const resetSelectedOffice = () => {
    setSelectedOffice(null);
  };

  //#endregion

  //#region PageHistory

  const [pageHistory, setPageHistory] = useState([pageHistoryDefaultValue]);

  /**
   * Adds a page to the end of history
   * @param {text} pageId a unique id
   * @param {text} pageName the display name used in the breadcrumb menu
   */
  const addToHistory = (pageId, pageName) => {
    GenerateVirtualPageView(pageHistory, pageName);

    setPageHistory([
      ...pageHistory,
      {
        pageId: pageId,
        pageNumber: pageHistory.length + 1,
        pageName: pageName,
      },
    ]);
  };

  /**
   * Removes the latest page from history, unless its the first page
   */
  const removeLastFromHistory = () => {
    if (pageHistory.length <= 1) {
      return;
    }

    const elementToRemove = pageHistory.pop();
    const updatedHistory = pageHistory.filter(
      (history) => history.pageNumber !== elementToRemove.pageNumber
    );

    GenerateVirtualPageView(updatedHistory);

    setPageHistory(updatedHistory);
  };

  /**
   * Finds the page with the given number, and removes the pages after it
   * @param {number} pageNumber
   */
  const goToPageInHistory = (pageNumber) => {
    const updatedHistory = pageHistory.filter(
      (history) => history.pageNumber <= pageNumber
    );

    if (pageNumber <= 2) {
      resetSetValues();
    }

    GenerateVirtualPageView(updatedHistory);

    setPageHistory(updatedHistory);
  };

  // Render the last page in the history list. Triggers when pageHistory is changed
  useEffect(() => {
    let lastPageInHistory = pageHistory[pageHistory.length - 1];
    if (lastPageInHistory === undefined) {
      return;
    }

    let pages = document.getElementsByClassName("segmentPage");

    for (var i = 0; i < pages.length; i++) {
      let currentPage = pages[i];
      const currentPageId = currentPage.id;

      // hide or show the page
      if (currentPageId === lastPageInHistory.pageId) {
        currentPage.classList.remove("hidden");

        scrollToLastBreadcrumbElement(currentPageId);
      } else {
        currentPage.classList.add("hidden");
      }
    }
  }, [pageHistory]);

  const scrollToLastBreadcrumbElement = (currentPageId) => {
    // find the relevant breadcrumb list, and then the active element within
    var breadcrumbsElement = document.querySelector(
      "#" + currentPageId + " .col-12" + " :last-child" + " nav"
    );

    if (breadcrumbsElement !== null) {
      breadcrumbsElement.scrollLeft += 1000;
    }
  };

  //#endregion

  //#region Breadcrumbs

  /**
   * Creates the breadcrumb elements and the button for moving back in history
   * @param callback Optional function paramter that triggers when clicking breadcrumb elements or the back button
   */
  const createBreadCrumbs = (currentPageId, callback) => {
    let path = [];
    for (let index = 1; index < pageHistory.length; index++) {
      const page = pageHistory[index];

      let link = page.pageName;
      let isActivePage = true;

      if (index < pageHistory.length - 1) {
        isActivePage = false;

        link = (
          <button
            className="breadcrumb__history-btn"
            aria-label="Tilbake til forrige side"
            onClick={() => {
              if (callback !== undefined) {
                callback();
              }
              goToPageInHistory(page.pageNumber);
            }}
            tabIndex="0"
          >
            {page.pageName + " > "} 
          </button>
        );
      }

      if (isActivePage === false) {
        path.push(
          <li key={"pathItem-" + index} className="breadcrumb__item">
            {link}
          </li>
        );
      } else {
        path.push(
          <li
            key={"pathItem-" + index}
            className="breadcrumb__item active"
            aria-current="page"
          >
            {link}
          </li>
        );
      }
    }

    return (
      <div className="book-meeting__breadcrumb">
          <nav
            className="breadcrumb-nav no-scrollbar"
            aria-label="Navigeringslinker"
            onScroll={(e) => breadcrumbScrollEventListener(e, currentPageId)}
          >
            <ol className="breadcrumb-row">{path}</ol>
          </nav>
      </div>
    );
  };

  const breadcrumbScrollEventListener = (event, currentPageId) => {
    /*
		Based on: 
		https://codepen.io/pawanvora/pen/MXVNgj
		*/

    let scrollLeft = event.target.scrollLeft;

    let navWidth = document.querySelector(
      "#" + currentPageId + " .breadcrumb-scrolling-wrapper > nav"
    ).scrollWidth;

    // Since the breadcrumbs are placed on multiple segment pages, we need to loop here
    let elements = document.querySelectorAll(".breadcrumb-scrolling-wrapper");
    elements.forEach((element) => {
      const currentPageScrollWrapperWidth = element.scrollWidth;
      if (currentPageScrollWrapperWidth === 0) {
        return;
      }

      // if fully scrolled to the right
      if (scrollLeft + currentPageScrollWrapperWidth >= navWidth) {
        element.classList.add("remove-fadeout-right");
      } else {
        element.classList.remove("remove-fadeout-right");
      }

      // if scrolled some to the right
      if (scrollLeft > 0) {
        element.classList.add("fadeout-left");
      } else {
        element.classList.remove("fadeout-left");
      }
    });
  };

  //#endregion

  //#region Search

  const [adviserSearchWord, setAdviserSearchWord] = useState("");
  const [searchResult, setSearchResult] = useState([]);

  const searchEmployees = (nextPageId, nextPageName) => {
    if (adviserSearchWord === "") {
      setSearchResult([]);
      return;
    }

    // showing spinner while searching
    setSearchResult(
      <div className="col-12">
        <LoadingSpinner loading={true} />
      </div>
    );

    let tempSearchResult = [];
    for (
      let officeIndex = 0;
      officeIndex < availableOfficesList.allOffices.length;
      officeIndex++
    ) {
      const office = availableOfficesList.allOffices[officeIndex];

      for (
        let employeeIndex = 0;
        employeeIndex < office.employeesInOffice.length;
        employeeIndex++
      ) {
        const employee = office.employeesInOffice[employeeIndex];

        const elementNameLower = employee.employeeName.toLowerCase().trim();
        const adviserSearchWordLower = adviserSearchWord.toLowerCase().trim();

        if (elementNameLower.indexOf(adviserSearchWordLower) !== -1) {
          const optionElement = (
            <SmallOptionBoxWithImage
              key={officeIndex + "-" + employeeIndex}
              heading={employee.employeeName}
              subHeading={office.officeName}
              imageSrc={employee.employeeImageUrl}
              clickHandler={() =>
                addSelectedSearchResult(
                  office,
                  employee,
                  nextPageId,
                  nextPageName
                )
              }
            />
          );
          tempSearchResult.push(optionElement);
        }
      }
    }

    // if no results are found, display message
    if (tempSearchResult.length === 0) {
      const nothingFoundElement = (
        <div key={"no-search-results"} className="col-12">
          <p>
            Vi har ingen rådgivere som heter dette, vennligst se om du har
            skrevet navnet riktig.
          </p>
        </div>
      );

      tempSearchResult.push(nothingFoundElement);
    }

    setSearchResult(tempSearchResult);
  };

  const clearSearch = () => {
    setAdviserSearchWord("");
    setSearchResult([]);
  };

  //#endregion

  //#region RenderFunctions

  const createOfficeList = (nextPageId, nextPageName) => {
    const officeList = availableOfficesList.allOffices.map(
      function (officeItem, index) {
        return (
          <BigOptionBox
            bootstrapSize="col-12 col-sm-6 col-lg-4"
            key={index}
            boxHeading={officeItem.officeName}
            clickHandler={() =>
              addSelectedOffice(officeItem, nextPageId, nextPageName)
            }
          />
        );
      });

    return <div className="book-meeting--small-button-list"><ul className="book-meeting__list">{officeList}</ul></div>;
  };

  const createEmployeesInSelectedOfficeList = (nextPageId, nextPageName) => {
    if (selectedOffice === null) {
      return;
    }

    const employeeList = selectedOffice.employeesInOffice.map(
      function (employeeItem, index) {
        return (
          <SmallOptionBoxWithImage
            key={index}
            heading={employeeItem.employeeName}
            imageSrc={employeeItem.employeeImageUrl}
            clickHandler={() =>
              addSelectedEmployee(employeeItem, nextPageId, nextPageName)
            }
          />
        );
      });
    return (
      <div className="book-meeting--advisor-list">
        <ul className="book-meeting__list">{employeeList}</ul>
      </div>
    );
  };

  const createAvailableChannelsForSelectedOffice = (
    nextPageId,
    nextPageName
  ) => {
    if (selectedOffice === null) {
      return;
    }

    const channelList = selectedOffice.channels.map(
      function (channelItem, index) {
        let [boxHeading, iconUrl] = selectChannelTextAndIcon(channelItem);
        if (boxHeading === "" && iconUrl === "") {
          return null;
        }

        return (
          <BigOptionBox
            key={index}
            boxHeading={boxHeading}
            boxIconPath={iconUrl}
            clickHandler={() =>
              addSelectedChannel(channelItem, nextPageId, nextPageName)
            }
          />
        );
      });

    return <ul className="book-meeting__list">{channelList}</ul>;
  };

  const createSelectedEmployeeCard = () => {
    if (selectedOffice === null || selectedEmployee === null) {
      return;
    }

    return (
      <SelectedEmployee
        employeeName={selectedEmployee.employeeName}
        employeeImageUrl={selectedEmployee.employeeImageUrl}
        officeName={selectedOffice.officeName}
      />
    );
  };

  const createAvailableDaysFromSelectedEmployee = (
    nextPageId,
    nextPageName
  ) => {
    if (selectedEmployee === null) {
      return;
    }

    const employeeAvailableDatesList =
      selectedEmployee.employeeAvailableDates.map(function (dateItem, index) {
        return (
          <BigOptionBox
            key={index}
            boxHeading={dateItem.datePretty}
            clickHandler={() =>
              addSelectedDate(dateItem, nextPageId, nextPageName)
            }
          />
        );
      });
    return (
      <div className="book-meeting--small-button-list">
        <ul className="book-meeting__list">{employeeAvailableDatesList}</ul>
      </div>
    );
  };

  const createAvailableDaysFromSelectedOffice = (nextPageId, nextPageName) => {
    if (selectedOffice === null) {
      return;
    }

    const availableDatesList = selectedOffice.availableDates.map(
      function (dateItem, index) {
        return (
          <BigOptionBox    
            key={index}
            boxHeading={dateItem.datePretty}
            clickHandler={() =>
              addSelectedDate(dateItem, nextPageId, nextPageName)
            }
          />
        );
      },
    );
    return (
      <div className="book-meeting--small-button-list">
        <ul className="book-meeting__list">{availableDatesList}</ul>
      </div>
    );
  };

  const createAvailableTimesFromSelectedDate = (nextPageId, nextPageName) => {
    if (selectedDate === null) {
      return;
    }

    const availableHoursList = selectedDate.availableHours.map(
      function (hour, index) {
        return (
          <BigOptionBox
            key={index}
            boxHeading={hour}
            clickHandler={() => addSelectedTime(hour, nextPageId, nextPageName)}
          />
        );
      },
    );
    return (
      <div className="book-meeting--small-button-list">
        <ul className="book-meeting__list">{availableHoursList}</ul>
      </div>
    );
  };

  const createAvailableChannelsFromAnyOffice = (
    inBankPageId,
    phonePageId,
    webPageId,
    inBankNextPageName,
    otherNextPageName
  ) => {
    let availableChannels = [];

    for (
      let index = 0;
      index < availableOfficesList.allOffices.length;
      index++
    ) {
      const office = availableOfficesList.allOffices[index];
      const officeChannels = office.channels;

      for (
        let channelIndex = 0;
        channelIndex < officeChannels.length;
        channelIndex++
      ) {
        const channel = officeChannels[channelIndex];
        if (availableChannels.indexOf(channel) === -1) {
          availableChannels.push(channel);
        }
      }
    }

    const availableChannelsList = availableChannels.map(
      function (channelItem, index) {
        let nextPageId = "";
        let nextPageName = otherNextPageName;

        switch (channelItem) {
          case 0: // In the bank
            nextPageId = inBankPageId;
            nextPageName = inBankNextPageName;
            break;
          case 1: // on the phone
            nextPageId = phonePageId;
            break;
          case 2: // web meeting
            nextPageId = webPageId;
            break;
          default:
            return null;
        }

        let [boxHeading, iconUrl] = selectChannelTextAndIcon(channelItem);
        if (boxHeading === "" && iconUrl === "") {
          return null;
        }

        return (
          <BigOptionBox
            bootstrapSize="col-12 col-md-6 col-xl-4"
            key={index}
            boxHeading={boxHeading}
            boxIconPath={iconUrl}
            clickHandler={() =>
              addSelectedChannel(channelItem, nextPageId, nextPageName)
            }
          />
        );
      });

    return (
      <div className="book-meeting--small-button-list">
        <ul className="book-meeting__list">{availableChannelsList}</ul>
      </div>
    );};

  const createOfficeListFromSelectedChannel = (nextPageId, nextPageName) => {
    if (selectedChannel === null) {
      return;
    }

    const officeList = availableOfficesList.allOffices.map(
      function (officeItem, index) {
        if (officeItem.channels.indexOf(selectedChannel) === -1) {
          return null;
        }

        return (
          <BigOptionBox
            key={index}
            boxHeading={officeItem.officeName}
            clickHandler={() =>
              addSelectedOffice(officeItem, nextPageId, nextPageName)
            }
          />
        );
      }
    );

    return (
      <div className="book-meeting--small-button-list">
        <ul className="book-meeting__list">{officeList}</ul>
      </div>
    );
  };

  const createAvailableDaysFromUnknownOffice = (nextPageId, nextPageName) => {
    let unknownOfficeList = [];
    if (selectedChannel === 1) {
      unknownOfficeList = availableOfficesList.phoneChannelOffices;
    } else if (selectedChannel === 2) {
      unknownOfficeList = availableOfficesList.webChannelOffices;
    } else {
      return null;
    }
    const availableDaysFromUnknownOfficeList = unknownOfficeList.map(
      function (dateItem, index) {
        return (
          <BigOptionBox
            key={index}
            boxHeading={dateItem.datePretty}
            clickHandler={() =>
              addSelectedDate(dateItem, nextPageId, nextPageName)
            }
          />
        )
      });
    return (
      <div className="book-meeting--small-button-list">
        <ul className="book-meeting__list">
          {availableDaysFromUnknownOfficeList}
        </ul>
      </div>
    );
  };

  const renderSumUpText = () => {
    if (selectedDate === null) {
      return null;
    }
    
    let summeryHeader = <p className="book-meeting__paragraph-header">Oppsummering</p>;
    let whoWhereWhenSumUp = null;
    if (selectedEmployee !== null && selectedOffice !== null) {
      // phone or web
      if (selectedChannel === 1 || selectedChannel === 2) {
        whoWhereWhenSumUp = (
          <div>{summeryHeader}
          <p>
            Velkommen til møte med{" "}
            <strong>{selectedEmployee.employeeName}</strong> på{" "}
            {selectedChannel === 1 ? "telefon" : "digitalt møte"} den{" "}
            {selectedDate.datePretty} kl. {selectedTime}.
          </p>
          </div>
        );
      }
      // in the bank
      else {
        whoWhereWhenSumUp = (
        <div>{summeryHeader}
          <p>
            Velkommen til møte med{" "}
            <strong>{selectedEmployee.employeeName}</strong> på{" "}
            {selectedOffice.officeName} den {selectedDate.datePretty} kl.{" "}
            {selectedTime}.
          </p>
        </div>
      );
    }
    } else if (selectedEmployee === null && selectedOffice !== null) {
      whoWhereWhenSumUp = (
        <div>{summeryHeader}
          <p>
            Du vil møte en rådgiver på {selectedOffice.officeName} den{" "}
            {selectedDate.datePretty} kl. {selectedTime}.
          </p>
        </div>
      );
    } else if (selectedEmployee === null && selectedOffice === null) {
      if (selectedChannel === 1 || selectedChannel === 2) {
        whoWhereWhenSumUp = (
          <div>{summeryHeader}
            <p>
              Du blir ringt av en rådgiver den {selectedDate.datePretty} kl.{" "}
              {selectedTime}. Dette er et{" "}
              {selectedChannel === 1 ? "telefonmøte" : "digitalt møte"}.
            </p>
          </div>
        );
      }
    }

    let subjectList = null;
    if (
      loanCheckbox ||
      savingCheckbox ||
      insuranceCheckbox ||
      privateEconomyCheckbox ||
      otherTextArea !== ""
    ) {
      subjectList = (
        <ul>
          {loanCheckbox === true ? <li>Lån</li> : null}
          {savingCheckbox === true ? <li>Sparing</li> : null}
          {insuranceCheckbox === true ? <li>Forsikring</li> : null}
          {privateEconomyCheckbox === true ? (
            <li>Andre banktjenester</li>
          ) : null}
          {otherTextArea !== "" ? <li>Annet: {otherTextArea}</li> : null}
        </ul>
      );
    }

    return (
      <>
        {whoWhereWhenSumUp}

        {subjectList !== null ? <p>I møtet går vi gjennom:</p> : null}
        {subjectList}
      </>
    );
  };

  //#endregion

  //#region Various

  const selectChannelTextAndIcon = (channelId) => {
    let channelText = "";
    let iconUrl = "";

    switch (channelId) {
      case 0:
        channelText = "I banken";
        iconUrl = inBankIconUrl;
        break;
      case 1:
        channelText = "På telefon";
        iconUrl = onThePhoneIconUrl;
        break;
      case 2:
        channelText = "Digitalt møte";
        iconUrl = webMeetingIconUrl;
        break;
      default:
        break;
    }

    return [channelText, iconUrl];
  };

  const selectChannelForSumUpText = (channelId) => {
    let channelText = "";

    switch (channelId) {
      case 0:
        channelText = "møte i banken";
        break;
      case 1:
        channelText = "telefonmøte";
        break;
      case 2:
        channelText = "digitalt møte";
        break;
      default:
        break;
    }

    return channelText;
  };

  //#endregion

  const [loanCheckbox, setLoanCheckbox] = useState(false);
  const [savingCheckbox, setSavingCheckbox] = useState(false);
  const [insuranceCheckbox, setInsuranceCheckbox] = useState(false);
  const [privateEconomyCheckbox, setPrivateEconomyCheckbox] = useState(false);
  const [otherTextArea, setOtherTextArea] = useState("");

  const [nameTextInput, setNameTextInput] = useState("");
  const [nameTextIsValid, setNameTextIsValid] = useState(true);

  const [phoneTextInput, setPhoneTextInput] = useState("");
  const [phoneTextIsValid, setPhoneTextIsValid] = useState(true);

  const [emailTextInput, setEmailTextInput] = useState("");
  const [emailTextIsValid, setEmailTextIsValid] = useState(true);

  const [inputIsValid, setInputIsValid] = useState(true);

  return (
    <div className="App container book-meeting">
      {/* Landing page */}
      <SegmentPage
        pageName="landingPage"
        isVisible={true}
        pageHeaderTitle={"Book møte med en rådgiver"}
        showError={failedToRetrievedOfficeList}
        errorHeaderTitle={"Oi! Det oppstod tekniske problemer. Prøv igjen."}
        errorMessage={<ErrorMessage />}
      >
        <LoadingSpinner
          loading={isFetchingAvailableOfficeList}
        ></LoadingSpinner>
      </SegmentPage>

      {/* START */}
      <SegmentPage
        pageName="notFoundPage"
        isVisible={false}
        pageHeaderTitle={"Det er dessverre ingen ledige rådgivere"}
      >
        <div className="row">
          <NoAdvisersAvailableMessage contactCenterUrl={contactCenterUrl} />
        </div>
      </SegmentPage>
      <SegmentPage
        pageName="startPage"
        isVisible={false}
        pageHeaderTitle={"Hvem vil du møte?"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs(
          "startPage",
          resetSetValues
        )}
      >
        <div className="book-meeting--advisor-section">
          <div>
            <AdviserNotFoundErrorMessage
              failedToFindAdviser={failedToFindAdviser}
            />
            <h3>Velg ett av alternativene:</h3>
          </div>
        </div>
          <ul className="book-meeting__list">
            <BigOptionBox
              iconAltText="Søk etter rådgiver"
              boxHeading="Velg rådgiver"
              boxIconPath={chooseAdviserIconUrl}
              clickHandler={() =>
                addToHistory(
                  "chooseAdviser-firstPage",
                  "Velg rådgiver eller kontor"
                )
              }
            />

            <BigOptionBox
              iconAltText="Få tildelt tilfeldig rådgiver"
              boxHeading="Få tildelt rådgiver"
              boxIconPath={getAssignedAdviserIconUrl}
              clickHandler={() =>
                addToHistory(
                  "assignedEmployee-selectChannel",
                  "Hvor skal vi treffes?"
                )
              }
            />
          </ul>
      </SegmentPage>

      {/* CHOOSE ADVISER */}
      <SegmentPage
        pageName="chooseAdviser-firstPage"
        isVisible={false}
        pageHeaderTitle={"Velg rådgiver"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs(
          "chooseAdviser-firstPage",
          resetSetValues
        )}
      >
        <div className="row">
          {/* search box */}
          <SearchBox
            searchBoxId="adviser-search"
            searchBoxLabel="Søk etter rådgiver:"
            searchBoxPlaceholder="Søk etter rådgiver"
            searchContent={adviserSearchWord}
            handleChange={setAdviserSearchWord}
            executeSearch={() =>
              searchEmployees(
                "chooseAdviser-selectChannelPage",
                "Hvor skal vi treffes?"
              )
            }
            clearSearch={clearSearch}
          >
            {searchResult}
          </SearchBox>

          {/* office list */}
          <ContentCollection
            stylingClassname="inputCollectionFormElements"
            collectionHeading="Eller velg ett av våre lokale kontorer:"
          >
            {createOfficeList(
              "chooseAdviser-selectAdviserInOfficePage",
              "Velg rådgiver"
            )}
          </ContentCollection>
        </div>
      </SegmentPage>
      <SegmentPage
        pageName="chooseAdviser-selectAdviserInOfficePage"
        isVisible={false}
        pageHeaderTitle={"Velg rådgiver"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs(
          "chooseAdviser-selectAdviserInOfficePage",
        )}
      >
        <div className="row inputCollectionFormElements">
          {createEmployeesInSelectedOfficeList(
            "chooseAdviser-selectChannelPage",
            "Hvor skal vi treffes?"
          )}
        </div>
      </SegmentPage>
      <SegmentPage
        pageName="chooseAdviser-selectChannelPage"
        isVisible={false}
        pageHeaderTitle={"Hvor skal vi treffes?"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs("chooseAdviser-selectChannelPage")}
      >
        <div className="book-meeting--small-button-list">
          {createSelectedEmployeeCard()}

          <ContentCollection
            stylingClassname="inputCollectionFormElements"
            collectionHeading="Velg møtested:"
          >
            {createAvailableChannelsForSelectedOffice(
              "chooseAdviser-selectDatePage",
              "Velg dag"
            )}
          </ContentCollection>
        </div>
      </SegmentPage>
      <SegmentPage
        pageName="chooseAdviser-selectDatePage"
        isVisible={false}
        pageHeaderTitle={"Velg dag"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs("chooseAdviser-selectDatePage")}
      >
        <div className="row inputCollectionFormElements">
          <div className="book-meeting__textwrapper">
            <p>
              <strong>Din rådgiver:</strong>
              {selectedEmployee !== null
                ? " " + selectedEmployee.employeeName + " /"
                : ""}
                {selectedOffice !== null
                ? " " + selectedOffice.officeName
                : ""}
                <br></br>
              <strong>Ditt møtested:</strong>
              {selectedChannel !== null
                ? " " + selectChannelTextAndIcon(selectedChannel)[0]
                : ""}
            </p>
            <br></br>
            <strong>Her er de ledige dagene vi kan møte deg:</strong>
          </div>

          {createAvailableDaysFromSelectedEmployee(
            "shared-selectTimePage",
            "Velg tid"
          )}
        </div>
      </SegmentPage>

      {/* GET ASSIGNED ADVISER */}
      <SegmentPage
        pageName="assignedEmployee-selectChannel"
        isVisible={false}
        pageHeaderTitle={"Hvor skal vi treffes?"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs(
          "assignedEmployee-selectChannel",
          resetSetValues
        )}
      >
        <div className="row inputCollectionFormElements">
          <div className="book-meeting__textwrapper">
            <p className="book-meeting__sublabel">Velg møtested:</p>
          </div>

          {createAvailableChannelsFromAnyOffice(
            "assignedEmployee-selectOffice",
            "assignedEmployee-selectDatePage-unknownOffice",
            "assignedEmployee-selectDatePage-unknownOffice",
            "Velg kontor",
            "Velg dag"
          )}
        </div>
      </SegmentPage>
      <SegmentPage
        pageName="assignedEmployee-selectOffice"
        isVisible={false}
        pageHeaderTitle={"Velg kontor"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs("assignedEmployee-selectOffice")}
      >
        <div className="row inputCollectionFormElements">
          <div className="book-meeting__sublabel">            
            <p>
              Ditt møtested:
              {selectedChannel !== null
                ? " " + selectChannelTextAndIcon(selectedChannel)[0]
                : ""}
            </p>
            <p>Dette er kontorene vi kan møtes på:</p>
          </div>

          {createOfficeListFromSelectedChannel(
            "assignedEmployee-selectDatePage-knownOffice",
            "Velg dag"
          )}
        </div>
      </SegmentPage>
      <SegmentPage
        pageName="assignedEmployee-selectDatePage-knownOffice"
        isVisible={false}
        pageHeaderTitle={"Velg dag"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs(
          "assignedEmployee-selectDatePage-knownOffice",
          resetSelectedOffice
        )}
      >
        <div className="row inputCollectionFormElements">
          <div className="book-meeting__textwrapper">
            <p>
              Ditt møtested:
              {selectedChannel !== null
                ? " " + selectChannelTextAndIcon(selectedChannel)[0] + " /"
                : ""}
              {selectedOffice !== null ? " " + selectedOffice.officeName : ""}
            </p>
            <p>
              Her er de tilgjengelige dagene for{" "}
              {selectedOffice !== null
                ? "" + selectedOffice.officeName
                : "dette kontoret"}
              .
            </p>
          </div>

          {createAvailableDaysFromSelectedOffice(
            "shared-selectTimePage",
            "Velg tid"
          )}
        </div>
      </SegmentPage>
      <SegmentPage
        pageName="assignedEmployee-selectDatePage-unknownOffice"
        isVisible={false}
        pageHeaderTitle={"Velg dag"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs(
          "assignedEmployee-selectDatePage-unknownOffice"
        )}
      >
        <div className="row inputCollectionFormElements">
          <div className="book-meeting__textwrapper">
            <p>
              Ditt møtested:
              {selectedChannel !== null
                ? " " + selectChannelTextAndIcon(selectedChannel)[0]
                : ""}
            </p>
            <p>
              Her er de tilgjengelige dagene for{" "}
              {selectChannelForSumUpText(selectedChannel)}.
            </p>
          </div>

          {createAvailableDaysFromUnknownOffice(
            "shared-selectTimePage",
            "Velg tid"
          )}
        </div>
      </SegmentPage>

      {/* SHARED */}
      <SegmentPage
        pageName="shared-selectTimePage"
        isVisible={false}
        pageHeaderTitle={"Velg tid"}
        pageHeaderDescription={""}
        breadCrumbs={createBreadCrumbs("shared-selectTimePage")}
      >
        <div className="row inputCollectionFormElements">
          <div className="book-meeting__textwrapper">
            <p>
              <strong>Valgt dato:</strong>
              {selectedDate !== null
                ? " " + selectedDate.datePretty
                : ""}
            </p>
            <p>
              <br />
              <strong>Her ser du ledige tider denne dagen.</strong>
              <br />
              Finner du ikke noe som passer? Gå tilbake og velg en annen dag.
            </p>
          </div>

          {createAvailableTimesFromSelectedDate(
            "shared-additionalInformation",
            "Bekreftelse"
          )}
        </div>
      </SegmentPage>
      <SegmentPage
        pageName="shared-additionalInformation"
        isVisible={false}
        pageHeaderTitle={"Litt om deg"}
        pageHeaderDescription={
          "For at din rådgiver skal kunne forberede seg til møtet, trenger vi litt informasjon om deg."
        }
        breadCrumbs={createBreadCrumbs("shared-additionalInformation")}
      >
        <div className="row meeting-info-summary">
          <ContentCollection
            stylingClassname="row inputCollectionFormElements"
            collectionHeading="Hva vil du snakke med oss om?"
          >
            <div className="col-12 summary-text meeting-info-summary">
              <div className="row">
                <Checkbox
                  bootstrapSize="col-12 col-lg-auto"
                  checkboxId="meeting-info-loan-checkbox"
                  checkboxLabel="Lån"
                  checkboxValue="loan"
                  checkboxAriaLabel="Avkrysningsrute for lån"
                  renderAsInline={false}
                  handleChange={() => setLoanCheckbox(!loanCheckbox)}
                  checkboxChecked={loanCheckbox}
                />
                <Checkbox
                  bootstrapSize="col-12 col-lg-auto"
                  checkboxId="meeting-info-saving-checkbox"
                  checkboxLabel="Sparing"
                  checkboxValue="saving"
                  checkboxAriaLabel="Avkrysningsrute for sparing"
                  renderAsInline={false}
                  handleChange={() => setSavingCheckbox(!savingCheckbox)}
                  checkboxChecked={savingCheckbox}
                />
                <Checkbox
                  bootstrapSize="col-12 col-lg-auto"
                  checkboxId="meeting-info-insurance-checkbox"
                  checkboxLabel="Forsikring"
                  checkboxValue="insurance"
                  checkboxAriaLabel="Avkrysningsrute for forsikring"
                  renderAsInline={false}
                  handleChange={() => setInsuranceCheckbox(!insuranceCheckbox)}
                  checkboxChecked={insuranceCheckbox}
                />
                <Checkbox
                  bootstrapSize="col-12 col-lg-auto"
                  checkboxId="meeting-info-privateEconomy-checkbox"
                  checkboxLabel="Andre banktjenester"
                  checkboxValue="privateEconomy"
                  checkboxAriaLabel="Avkrysningsrute for andre banktjenester"
                  renderAsInline={false}
                  handleChange={() =>
                    setPrivateEconomyCheckbox(!privateEconomyCheckbox)
                  }
                  checkboxChecked={privateEconomyCheckbox}
                />
                <TextArea
                  textAreaId="meeting-info-other-textarea"
                  textAreaLabel="Andre tema til møtet?"
                  textAreaPlaceholderText=""
                  textAreaAriaLabel="Andre tema til møtet"
                  textAreaContent={otherTextArea}
                  handleChange={setOtherTextArea}
                />
              </div>
            </div>
          </ContentCollection>

          <ContentCollection>
          <fieldset>
            <legend className="book-meeting__fieldset-legend">Din kontaktinformasjon</legend>        
            <div className="col-12 summary-text">
              <TextInput
                textInputId="meeting-name-textInput"
                textInputLabel="Navn "
                textInputType="text"
                textInputName="customerName"
                textInputAriaLabel="Skriv navnet ditt her"
                textInputIsRequired={true}
                invalidMessage={"Dette feltet er påkrevd."}
                textInputContent={nameTextInput}
                pattern={"^(?!\\s*$).+"}
                needValidation={true}
                handleChange={setNameTextInput}
                autoCompleteIsOn={true}
                textInputIsValid={nameTextIsValid}
              />
              <TextInput
                textInputId="meeting-phone-textInput"
                textInputLabel="Mobilnummer "
                textInputType="tel"
                textInputName="phoneNumber"
                textInputAriaLabel="Skriv mobilnummeret ditt her"
                textInputIsRequired={true}
                invalidMessage="Ugyldig mobilnummer. Mobilnummer må inneholde 8 siffer og kan ikke ha bokstaver."
                pattern={"^(0047|\\+47|47)?[4,9]\\d{7}$"}
                textInputContent={phoneTextInput}
                needValidation={true}
                handleChange={setPhoneTextInput}
                autoCompleteIsOn={true}
                textInputIsValid={phoneTextIsValid}
              />
              <TextInput
                textInputId="meeting-email-textInput"
                textInputLabel="Epostadresse "
                textInputType="email"
                textInputName="email"
                textInputAriaLabel="Skriv eposten din her"
                invalidMessage={
                  "Ugyldig epost adresse. Epost må skrives på formatet: bruker@eksempel.com"
                }
                textInputIsRequired={true}
                pattern={
                  "^[a-zA-Z0-9.-_]{1,}@[a-zA-Z0-9.-]{1,}[.]{1}[a-zA-Z]{2,}$"
                }
                textInputContent={emailTextInput}
                needValidation={true}
                handleChange={setEmailTextInput}
                autoCompleteIsOn={true}
                textInputIsValid={emailTextIsValid}
              />
            </div>
            </fieldset>
          </ContentCollection>

          <ContentCollection
            stylingClassname="row inputCollectionFormElements"
            collectionHeading=""
          >
            <div className="col-12 summary-textbox">
              {renderSumUpText()}
              <p>
                Trykk på knappen under for å avtale møtet. Du får en SMS med
                møtedetaljene kort tid etterpå.
                <br />
                <br />
                Vi gleder oss til å se deg!
              </p>
            </div>
          </ContentCollection>

          <div className="col-12">
            {isBookingNewMeeting === false ? (
              <button
                className="button button--primary"
                aria-label="Send bestilling av møtet"
                onClick={() => submitBooking()}
                disabled={isBookingNewMeeting}
              >
                Book møte
              </button>
            ) : (
              <LoadingSpinner loading={isBookingNewMeeting}></LoadingSpinner>
            )}
          </div>
        </div>
      </SegmentPage>
      <SegmentPage
        pageName="shared-verificationOnSubmit"
        isVisible={false}
        pageHeaderTitle={"Takk for din bestilling"}
        showError={failedToBookMeeting}
        errorHeaderTitle={"Klarte ikke å bestille møtet"}
        errorMessage={<ErrorMessage />}
      >
        {renderSumUpText()}
        <p>Du vil straks få en bekreftelse på SMS.</p>

        <p>Trykk på knappen under for å gå tilbake til start.</p>

        <button
          className="btn btn-primary button-primary button-secondary"
          aria-label="Knapp for å komme tilbake til startsiden"
          onClick={() => resetToStart()}
        >
          Tilbake
        </button>
      </SegmentPage>
    </div>
  );
}

export default App;
